import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import FloatingDock from '@/components/layout/FloatingDock';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { 
  Trophy, Award, Crown, Medal, Star, Sparkles,
  Palette, Laugh, Newspaper, Mic, BookOpen, 
  ChevronRight, Users, ArrowLeft, Flame, BadgeCheck
} from 'lucide-react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Award category configurations
const CATEGORY_CONFIG = {
  artistic: { icon: Palette, color: "#8b5cf6", emoji: "🎨", name: "Artistic Creativity" },
  comedic: { icon: Laugh, color: "#f59e0b", emoji: "😂", name: "Comedic" },
  journalistic: { icon: Newspaper, color: "#3b82f6", emoji: "📰", name: "Journalistic" },
  podcast: { icon: Mic, color: "#ef4444", emoji: "🎙️", name: "Podcast" },
  inspirational: { icon: Sparkles, color: "#10b981", emoji: "✨", name: "Inspirational" },
  educational: { icon: BookOpen, color: "#06b6d4", emoji: "📚", name: "Educational" }
};

const HallOfFame = ({ user }) => {
  const navigate = useNavigate();
  const [hallOfFame, setHallOfFame] = useState([]);
  const [topCreators, setTopCreators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(() => {
    fetchHallOfFame();
  }, []);

  const fetchHallOfFame = async () => {
    setLoading(true);
    try {
      const [hofRes, creatorsRes] = await Promise.all([
        axios.get(`${API_URL}/awards/hall-of-fame`),
        axios.get(`${API_URL}/awards/top-creators`)
      ]);
      
      setHallOfFame(hofRes.data.hall_of_fame || []);
      setTopCreators(creatorsRes.data.creators || []);
    } catch (error) {
      console.error('Failed to load Hall of Fame');
    } finally {
      setLoading(false);
    }
  };

  const filteredHoF = selectedCategory === 'all' 
    ? hallOfFame 
    : hallOfFame.filter(item => item.category === selectedCategory);

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-yellow-50 to-orange-50 dark:from-gray-900 dark:via-amber-900/10 dark:to-gray-900">
      {/* Decorative Header */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-yellow-400/20 via-amber-500/20 to-orange-400/20" />
        <div className="absolute top-10 left-10 w-32 h-32 bg-yellow-400/30 rounded-full blur-3xl" />
        <div className="absolute top-20 right-20 w-40 h-40 bg-amber-500/30 rounded-full blur-3xl" />
        
        <div className="relative max-w-6xl mx-auto px-4 py-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="flex justify-center mb-4">
              <div className="relative">
                <div className="w-24 h-24 rounded-full bg-gradient-to-br from-yellow-400 to-amber-500 flex items-center justify-center shadow-2xl">
                  <Trophy className="w-12 h-12 text-white" />
                </div>
                <div className="absolute -top-2 -right-2 w-8 h-8 bg-gradient-to-br from-yellow-300 to-amber-400 rounded-full flex items-center justify-center">
                  <Star className="w-4 h-4 text-white" />
                </div>
              </div>
            </div>
            <h1 className="text-4xl md:text-5xl font-heading font-black text-gray-900 dark:text-white mb-2">
              Hall of Fame
            </h1>
            <p className="text-lg text-gray-600 dark:text-gray-400">
              Celebrating Socyy's most decorated content creators
            </p>
          </motion.div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6 pb-24">
        {/* Top Creators Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-12"
        >
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
            <Crown className="w-6 h-6 text-yellow-500" />
            Most Decorated Creators
          </h2>
          
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[1, 2, 3].map(i => (
                <div key={i} className="h-48 bg-gray-200 dark:bg-gray-700 rounded-3xl animate-pulse" />
              ))}
            </div>
          ) : topCreators.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {topCreators.slice(0, 3).map((creator, idx) => (
                <motion.div
                  key={creator.user_id}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: idx * 0.1 }}
                >
                  <Card className={`p-6 rounded-3xl relative overflow-hidden ${
                    idx === 0 
                      ? 'bg-gradient-to-br from-yellow-100 to-amber-100 dark:from-yellow-900/30 dark:to-amber-900/30 border-2 border-yellow-400'
                      : idx === 1
                      ? 'bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-700 border-2 border-gray-400'
                      : 'bg-gradient-to-br from-orange-100 to-amber-100 dark:from-orange-900/30 dark:to-amber-900/30 border-2 border-orange-400'
                  }`}>
                    {/* Rank Badge */}
                    <div className={`absolute top-4 right-4 w-10 h-10 rounded-full flex items-center justify-center font-bold text-white ${
                      idx === 0 ? 'bg-yellow-500' : idx === 1 ? 'bg-gray-500' : 'bg-orange-500'
                    }`}>
                      {idx === 0 ? <Crown className="w-5 h-5" /> : idx + 1}
                    </div>
                    
                    <div className="flex flex-col items-center text-center">
                      <Link to={`/profile/${creator.username}`}>
                        <Avatar className={`w-20 h-20 border-4 ${
                          idx === 0 ? 'border-yellow-400' : idx === 1 ? 'border-gray-400' : 'border-orange-400'
                        }`}>
                          <AvatarImage src={creator.avatar} />
                          <AvatarFallback className="text-2xl bg-gradient-to-br from-yellow-400 to-amber-500 text-white">
                            {creator.name?.charAt(0)}
                          </AvatarFallback>
                        </Avatar>
                      </Link>
                      
                      <div className="mt-3">
                        <Link to={`/profile/${creator.username}`} className="hover:underline">
                          <h3 className="font-bold text-lg text-gray-900 dark:text-white flex items-center justify-center gap-1">
                            {creator.name}
                            {creator.is_verified && <BadgeCheck className="w-4 h-4 text-blue-500" />}
                          </h3>
                        </Link>
                        <p className="text-sm text-gray-500">@{creator.username}</p>
                      </div>
                      
                      <div className="mt-4 flex items-center gap-2">
                        <Trophy className="w-5 h-5 text-yellow-500" />
                        <span className="font-bold text-xl text-gray-900 dark:text-white">{creator.total_awards}</span>
                        <span className="text-gray-500">awards</span>
                      </div>
                      
                      {/* Award Category Badges */}
                      <div className="mt-3 flex flex-wrap justify-center gap-1">
                        {creator.categories?.slice(0, 4).map(cat => {
                          const config = CATEGORY_CONFIG[cat];
                          return (
                            <span
                              key={cat}
                              className="text-lg"
                              title={config?.name}
                            >
                              {config?.emoji}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  </Card>
                </motion.div>
              ))}
            </div>
          ) : (
            <Card className="p-8 text-center bg-white dark:bg-gray-800 rounded-3xl">
              <Trophy className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">No winners yet</h3>
              <p className="text-gray-500">Start voting to help creators win awards!</p>
            </Card>
          )}
        </motion.div>

        {/* Category Filter */}
        <div className="flex flex-wrap gap-2 mb-6">
          <button
            onClick={() => setSelectedCategory('all')}
            className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${
              selectedCategory === 'all'
                ? 'bg-yellow-500 text-white'
                : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
            }`}
          >
            All Awards
          </button>
          {Object.entries(CATEGORY_CONFIG).map(([key, config]) => (
            <button
              key={key}
              onClick={() => setSelectedCategory(key)}
              className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors flex items-center gap-1 ${
                selectedCategory === key
                  ? 'text-white'
                  : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
              style={selectedCategory === key ? { backgroundColor: config.color } : {}}
            >
              <span>{config.emoji}</span>
              {config.name}
            </button>
          ))}
        </div>

        {/* Hall of Fame List */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
            <Award className="w-6 h-6 text-yellow-500" />
            Award Winners
          </h2>
          
          {loading ? (
            <div className="space-y-4">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="h-24 bg-gray-200 dark:bg-gray-700 rounded-2xl animate-pulse" />
              ))}
            </div>
          ) : filteredHoF.length > 0 ? (
            <div className="space-y-4">
              {filteredHoF.map((winner, idx) => {
                const config = CATEGORY_CONFIG[winner.category];
                const Icon = config?.icon || Award;
                
                return (
                  <motion.div
                    key={winner.winner_id || idx}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: idx * 0.05 }}
                  >
                    <Card className="p-4 bg-white dark:bg-gray-800 rounded-2xl hover:shadow-lg transition-shadow">
                      <div className="flex items-center gap-4">
                        {/* Category Badge */}
                        <div
                          className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0"
                          style={{ backgroundColor: `${config?.color}20` }}
                        >
                          <span className="text-2xl">{config?.emoji}</span>
                        </div>
                        
                        {/* Winner Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-bold text-gray-900 dark:text-white">
                              {config?.name} Award
                            </span>
                            <span className="px-2 py-0.5 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 rounded-full text-xs font-semibold">
                              {winner.period === 'month' ? winner.month : winner.year}
                            </span>
                          </div>
                          <p className="text-sm text-gray-500 mt-0.5">
                            {winner.vote_count} votes
                          </p>
                        </div>
                        
                        {/* Winner Avatar */}
                        <Link 
                          to={`/profile/${winner.owner?.username}`}
                          className="flex items-center gap-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 p-2 rounded-xl transition-colors"
                        >
                          <Avatar className="w-12 h-12 border-2 border-yellow-400">
                            <AvatarImage src={winner.owner?.avatar} />
                            <AvatarFallback className="bg-gradient-to-br from-yellow-400 to-amber-500 text-white">
                              {winner.owner?.name?.charAt(0)}
                            </AvatarFallback>
                          </Avatar>
                          <div className="hidden sm:block">
                            <p className="font-semibold text-gray-900 dark:text-white flex items-center gap-1">
                              {winner.owner?.name}
                              {winner.owner?.is_verified && <BadgeCheck className="w-4 h-4 text-blue-500" />}
                            </p>
                            <p className="text-sm text-gray-500">@{winner.owner?.username}</p>
                          </div>
                          <ChevronRight className="w-5 h-5 text-gray-400" />
                        </Link>
                      </div>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
          ) : (
            <Card className="p-8 text-center bg-white dark:bg-gray-800 rounded-3xl">
              <Medal className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                No winners in this category yet
              </h3>
              <p className="text-gray-500">Be the first to win!</p>
            </Card>
          )}
        </motion.div>
      </div>

      <FloatingDock user={user} />
    </div>
  );
};

export default HallOfFame;
